import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Card from '../UI/Card';
import Button from '../UI/Button';
import Input from '../UI/Input';

export default function CourseCreate() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    courseCode: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = '수업 제목을 입력해주세요.';
    } else if (formData.title.length > 200) {
      newErrors.title = '수업 제목은 200자를 초과할 수 없습니다.';
    }

    if (formData.description && formData.description.length > 2000) {
      newErrors.description = '수업 설명은 2000자를 초과할 수 없습니다.';
    }

    if (formData.courseCode && formData.courseCode.length > 20) {
      newErrors.courseCode = '수업 코드는 20자를 초과할 수 없습니다.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      // 빈 문자열 필드 제거 (optional 필드의 경우)
      const requestData = { ...formData };
      if (!requestData.courseCode?.trim()) {
        delete requestData.courseCode;
      }
      if (!requestData.description?.trim()) {
        delete requestData.description;
      }

      const response = await api.post('/courses', requestData);

      if (response.data.success) {
        navigate(`/courses/${response.data.course.id}`);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || '수업 개설에 실패했습니다.';
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-neutral-800 mb-2">새 수업 만들기</h1>
        <p className="text-neutral-600">학생들이 참여할 수 있는 새로운 수업을 개설하세요.</p>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="수업 제목"
            name="title"
            value={formData.title}
            onChange={handleChange}
            error={errors.title}
            placeholder="예: 초등 3학년 과학"
            required
          />

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              수업 설명
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-neutral-200 focus:border-primary-500 focus:outline-none transition-colors duration-200 min-h-[120px]"
              placeholder="수업에 대한 설명을 입력하세요..."
            />
            {errors.description && (
              <p className="mt-1.5 text-sm text-danger-500">{errors.description}</p>
            )}
          </div>

          <Input
            label="수업 코드 (선택사항)"
            name="courseCode"
            value={formData.courseCode}
            onChange={handleChange}
            error={errors.courseCode}
            helper="비워두면 자동으로 생성됩니다."
            placeholder="예: SCI-3A-2024"
          />

          {errors.submit && (
            <div className="p-4 bg-danger-50 border border-danger-200 rounded-xl">
              <p className="text-sm text-danger-600">{errors.submit}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1"
            >
              {loading ? '생성 중...' : '수업 개설'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/courses')}
              disabled={loading}
            >
              취소
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
